import { memo, useCallback } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Badge, Button, CartContainer } from '@aimsos/ui';

import { formatTraitDate } from '../../utils/traitDates.js';

/**
 * One source of an insight (CT-5716), as a card like the prototype's: the type
 * chip, who, when — with the time only when the payload has one — how it turned
 * out, and "Open in History" when the source points at a History event and
 * there is a History tab to open.
 */
const InsightSourceRow = memo(function InsightSourceRow({ source, onOpenInHistory }) {
  const { t, i18n } = useTranslation('unified-profiles');
  const language = i18n?.language;

  const handleOpen = useCallback(() => onOpenInHistory?.(source.historyEventId), [onOpenInHistory, source]);

  // A day with no time is read as that LOCAL day, not as midnight UTC.
  const at = source.occurredAt && !source.hasTime ? `${source.occurredAt}T00:00:00` : source.occurredAt;
  const date = formatTraitDate(at, language);
  const time = source.hasTime && at ? new Date(at).toLocaleTimeString(language, { hour: 'numeric', minute: '2-digit' }) : null;
  const when = [date, time].filter(Boolean).join(' · ');

  return (
    <li data-testid="insight-source">
      <CartContainer size="S" variant="neutral">
        <div className="flex flex-col gap-1 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span data-testid="insight-source-kind">
              <Badge variant="neutral" size="small">
                {source.label}
              </Badge>
            </span>
            {source.person ? <span className="font-medium text-content">{source.person}</span> : null}
            {source.person && when ? (
              <span aria-hidden className="text-content-muted">
                ·
              </span>
            ) : null}
            {when ? <span className="text-content-secondary">{when}</span> : null}
          </div>
          {source.outcome ? <span className="text-content-secondary">{source.outcome}</span> : null}
          {source.historyEventId && onOpenInHistory ? (
            <div>
              <Button variant="ghost" size="sm" onClick={handleOpen}>
                {t('intelligence.insights.detail.openInHistory')}
              </Button>
            </div>
          ) : null}
        </div>
      </CartContainer>
    </li>
  );
});

export default InsightSourceRow;
